import { UIEvent, useCallback, useEffect, useRef } from "react";

interface UseInfiniteScrollParams {
  /** Se existe uma próxima página para carregar. */
  hasNextPage: boolean;
  /** Se alguma requisição de página já está em andamento. */
  isFetching: boolean;
  /** Dispara o carregamento da próxima página. */
  fetchNextPage: () => void;
  /** Distância (px) do fim da lista para antecipar o carregamento. */
  rootMargin?: number;
  /** Desliga o observer (ex.: enquanto o skeleton inicial está visível). */
  enabled?: boolean;
}

/**
 * Scroll infinito baseado em IntersectionObserver.
 *
 * Diferente do `onScroll` + comparação de `scrollHeight`, este hook:
 * - funciona mesmo quando a primeira página não preenche o container
 *   (o sentinela já nasce visível e dispara a página seguinte);
 * - não depende de igualdade exata de pixels (zoom/DPI fracionário);
 * - continua funcionando se quem rola for um ancestral do container;
 * - não dispara requisições concorrentes enquanto `isFetching` é true.
 *
 * Uso:
 *   const { sentinelRef, scrollRootRef } = useInfiniteScroll({ ... });
 *   <Content ref={scrollRootRef}>
 *     {items.map(...)}
 *     <div ref={sentinelRef} />
 *   </Content>
 */
export function useInfiniteScroll<
  TRoot extends HTMLElement = HTMLDivElement,
  TSentinel extends HTMLElement = HTMLDivElement
>({
  hasNextPage,
  isFetching,
  fetchNextPage,
  rootMargin = 200,
  enabled = true,
}: UseInfiniteScrollParams) {
  const sentinelRef = useRef<TSentinel | null>(null);
  const scrollRootRef = useRef<TRoot | null>(null);

  // Mantém a referência mais recente sem recriar o observer a cada render.
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;

  const canFetch = enabled && hasNextPage && !isFetching;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !canFetch) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fetchNextPageRef.current();
        }
      },
      {
        // `null` usa o viewport, que cobre o caso do container não ser
        // o elemento que efetivamente rola.
        root: scrollRootRef.current ?? null,
        rootMargin: `0px 0px ${rootMargin}px 0px`,
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
    // `canFetch` volta a true após cada página, reavaliando a interseção —
    // é isso que mantém o carregamento contínuo quando o sentinela segue visível.
  }, [canFetch, rootMargin]);

  /** Fallback para containers com `onScroll` já existente. */
  const handleScroll = useCallback(
    (event: UIEvent<TRoot>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight + rootMargin && canFetch) {
        fetchNextPageRef.current();
      }
    },
    [canFetch, rootMargin]
  );

  return { sentinelRef, scrollRootRef, handleScroll };
}

export default useInfiniteScroll;
